/**
 * @providesModule NavigationManager
 *
 */

import SkillzRouter from 'SkillzRouter';
import type {ReactNode} from 'react';
import {NativeModules} from 'react-native';
import ScreenNames from 'ScreenNames';

const SkillzModule = NativeModules.SkillzModule;

export type SceneTransition = 'slide' | 'fade' | 'none';

export type StackEntry = {
  component: ReactNode;
  animated: boolean;
  scene: string;
  enableGestures: boolean;
  sceneTransition: SceneTransition;
};

class NavigationEmitter {
  private listeners = new Set<() => void>();

  subscribe(listener: () => void) {
    this.listeners.add(listener);
  }

  unsubscribe(listener: () => void) {
    this.listeners.delete(listener);
  }

  emit() {
    this.listeners.forEach((fn) => fn());
  }
}

class NavigationManager {
  private static emitter = new NavigationEmitter();
  static stack: StackEntry[] = [];

  static currentHomeTab: string | null = null;

  // Screen tracking for telemetry
  private static currentScreenIdentifier: string = '';
  private static previousScreenIdentifier: string = '';

  static subscribe(fn: () => void) {
    NavigationManager.emitter.subscribe(fn);
  }

  static unsubscribe(fn: () => void) {
    NavigationManager.emitter.unsubscribe(fn);
  }

  private static updateStack(newStack: StackEntry[]) {
    NavigationManager.stack = newStack;
    NavigationManager.emitter.emit();
  }

  // Screens that have been migrated to ReactNavigationStack
  // These should not be pushed or replaced via legacy NavigationManager
  private static readonly MIGRATED_SCREENS = [
    'ReferralScreen',
    'PersonalInfo',
    'EmailManagement',
    'TrophyHome',
    'Player Profile Page',
    'EditProfileScreen',
    'EventDetailsScreen',
    'Withdraw',
    'LoyaltyStoreAddressInput',
    'EmailVerify',
    'SaveAccount',
  ];

  private static _warnIfPushingMigratedScreen(scene: string) {
    if (NavigationManager.MIGRATED_SCREENS.includes(scene)) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `Navigation Refactor: MIXED NAVIGATORS DETECTED; Action: pushComponentWithScene; Target: "${scene}"; Current Route: "${window.location.pathname}"; Explanation: This screen is migrated to react-navigation already. Consider using SkillzRouter.push() instead`
        );
      } else {
        console.error(
          `Navigation Error: Pushing migrated screen ${scene} via legacy navigator (web)`
        );
      }
    }
  }

  private static _warnIfReplacingMigratedScreen(scene: string) {
    if (NavigationManager.MIGRATED_SCREENS.includes(scene)) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `Navigation Refactor: MIXED NAVIGATORS DETECTED; Action: replaceComponent; Target: "${scene}"; Current Route: "${window.location.pathname}"; Explanation: This screen is migrated to react-navigation already. Consider using SkillzRouter.replace() instead`
        );
      } else {
        console.error(
          `Navigation Error: Replacing with migrated screen ${scene} via legacy navigator (web)`
        );
      }
    }
  }

  static pushComponentWithScene(
    component: ReactNode,
    animated: boolean,
    scene: string,
    enableGestures: boolean,
    sceneTransition: SceneTransition
  ) {
    if (scene) {
      NavigationManager._warnIfPushingMigratedScreen(scene);
    }
    // Make fake history state to make sure that browser back button has same history state
    // /home -> /account -> /account -> /account
    //                 push()     push()
    // /account -> /account -> /account -> /home
    //         pop()       pop()
    window.history.pushState(null, document.title, window.location.href);
    NavigationManager.updateStack([
      ...NavigationManager.stack,
      {component, animated, scene, enableGestures, sceneTransition},
    ]);
    NavigationManager.trackNavigation();
  }

  static push(
    component: ReactNode,
    animated: boolean,
    scene: string,
    enableGestures: boolean,
    sceneTransition: SceneTransition
  ) {
    NavigationManager.pushComponentWithScene(
      component,
      animated,
      scene,
      enableGestures,
      sceneTransition
    );
  }

  // TODO: Implement this method / different params from SkillzSDK
  static pushComponent() {}

  // TODO: Implement this method / from SkillzSDK
  static pushRoute() {}

  static replaceComponent(
    component: ReactNode,
    scene: string,
    sceneTransition: SceneTransition = 'none'
  ) {
    if (scene) {
      NavigationManager._warnIfReplacingMigratedScreen(scene);
    }

    const newEntry: StackEntry = {
      component,
      animated: true,
      scene,
      enableGestures: false,
      sceneTransition,
    };

    if (NavigationManager.stack.length > 0) {
      NavigationManager.updateStack([...NavigationManager.stack.slice(0, -1), newEntry]);
    } else {
      NavigationManager.updateStack([newEntry]);
    }
    NavigationManager.trackNavigation();
  }

  static replace(component: ReactNode, scene: string) {
    NavigationManager.replaceComponent(component, scene);
  }

  static replaceWithTransition(
    component: ReactNode,
    scene: string,
    sceneTransition: SceneTransition = 'none'
  ) {
    NavigationManager.replaceComponent(component, scene, sceneTransition);
  }

  static popNavigator() {
    if (NavigationManager.stack.length > 0) {
      NavigationManager.updateStack(NavigationManager.stack.slice(0, -1));
      NavigationManager.trackNavigation();
    } else {
      if (process.env.NODE_ENV === 'production') {
        console.error(`Navigation Error: popNavigator on ${window.location.pathname}`);
      } else {
        console.warn(
          `Navigation Refactor: MIXED NAVIGATORS DETECTED; Action: popNavigator; Current Route: "${window.location.pathname}"; Explanation: popNavigator() invoked when legacy stack is empty. Consider using SkillzRouter.back() instead`
        );
      }
    }
  }
  static pop() {
    NavigationManager.popNavigator();
  }

  static popToRoot() {
    if (NavigationManager.stack.length > 0) {
      NavigationManager.updateStack([]);
    }
    if (!window.location.pathname.includes('/home')) {
      const gameId = SkillzModule.App.GameId;
      const searchParamsString = window.location.search;
      SkillzRouter.replace(`/games/${gameId}/home${searchParamsString}`);
    }
    NavigationManager.trackNavigation();
  }

  static popToTop() {
    NavigationManager.popToRoot();
  }
  static popNavigatorToRoot() {
    NavigationManager.popToRoot();
  }
  static popEverythingToRoot() {
    NavigationManager.popToRoot();
  }

  // TODO: implement this method from SkillzSDK
  static resetToHomeScreen() {}

  static getRouteLength() {
    return NavigationManager.stack.length;
  }

  // TODO: Implement this method
  static pushSceneWithHomeAsRoot() {}

  /**
   * @deprecated not used anymore
   */
  static setDelegate() {}

  // TODO: Implement this method
  static skillzCanExit() {
    return false;
  }
  // TODO: Move it to side menu drawer controller or sth
  static isSideMenuEnabled() {
    return false;
  }
  static enableSideMenu() {}
  static disableSideMenu() {}
  static openSideMenu() {}
  static closeSideMenu() {}

  // TODO: Implement this method or remove usage
  static hideModal() {}

  // TODO: Implement replacement for this method and remove it
  static isPostMatchDisplayed() {}

  static getCurrentHomeTab() {
    return NavigationManager.currentHomeTab;
  }

  static setCurretHomeTab(tab: string) {
    const oldTab = NavigationManager.currentHomeTab;
    NavigationManager.currentHomeTab = tab;

    if (oldTab !== tab) {
      NavigationManager.trackNavigation();
    }

    return NavigationManager.currentHomeTab;
  }

  private static generateScreenIdentifier(): string {
    try {
      const currentHomeTab = NavigationManager.getCurrentHomeTab() || '';
      const currentScene =
        NavigationManager.stack.length > 0
          ? NavigationManager.stack[NavigationManager.stack.length - 1]?.scene || ''
          : '';

      return currentScene && currentHomeTab
        ? `${currentHomeTab}:${currentScene}`
        : currentScene || currentHomeTab || '';
    } catch (error) {
      console.error('[NavigationManager] Error generating screen identifier:', error);
      return '';
    }
  }

  private static updateScreenHistory(force: boolean = false): void {
    try {
      const newScreenIdentifier = NavigationManager.generateScreenIdentifier();

      if (force || newScreenIdentifier !== NavigationManager.currentScreenIdentifier) {
        NavigationManager.previousScreenIdentifier = NavigationManager.currentScreenIdentifier;
        NavigationManager.currentScreenIdentifier = newScreenIdentifier;
      }
    } catch (error) {
      console.error('[NavigationManager] Error updating screen history:', error);
    }
  }

  private static trackNavigation(): void {
    NavigationManager.updateScreenHistory(true);
  }

  static getCurrentScreenIdentifier(): string {
    NavigationManager.updateScreenHistory();
    return NavigationManager.currentScreenIdentifier;
  }

  static getPreviousScreenIdentifier(): string {
    return NavigationManager.previousScreenIdentifier;
  }

  static initializeScreenTracking(): void {
    NavigationManager.currentScreenIdentifier = NavigationManager.generateScreenIdentifier();
    NavigationManager.previousScreenIdentifier = '';
  }

  // TODO: Implement this method
  // Search in stack or in SkillzRouter screens
  // screens: {
  //   [ScreenNames.LEGACY_NAVIGATION_STACK]: 'games/:gid/home',
  //   [ScreenNames.RESULT]: 'games/:gid/tournament/:tid/result/:matchId',
  //   [ScreenNames.FRIEND_REFERRAL]: 'games/:gid/referral-friend',
  static popToRoute() {}

  //  TODO: Implement this method, directly from deprecated navigator
  static jumpTo() {}

  // TODO: Remove usage, use method directly
  static getNavigator(): typeof NavigationManager {
    return NavigationManager;
  }

  // TODO: Get information what is handleDeeplinkToLoyalty and displayLegacyTicketz and reimplement
  static displayTicketz() {
    const gameId = SkillzModule.App.GameId;
    SkillzRouter.push(`/games/${gameId}/ticketz`);
  }

  // TODO: Implement this method
  static isTicketzPresented() {}

  // TODO: Remove usage, use screen directly
  static displayTrophyHome() {}

  // TODO: Remove after implementing displayTrophyHome
  static isTrophyHomePresented() {}

  /**
   * @deprecated not used anymore
   */
  static getCurrentScene() {}

  // TODO: Implement this method / potentially remove it
  static getRouteWithName() {}

  // TODO: Implement this method / potentially remove it
  static getAllRoutesWithName() {}

  // Route patterns from ReactNavigationStack navConfig
  private static routePatterns: Record<string, string> = {
    [ScreenNames.ENTRY_SCREEN]: 'games/:gid/home',
    // [ScreenNames.STAKES_SELECTION]: 'games/:gid/templates/:template_id/stakes-selection',// Not implemented on web yet
    [ScreenNames.RESULT]: 'games/:gid/tournament/:tid/result/:matchId',
    [ScreenNames.FRIEND_REFERRAL]: 'games/:gid/referral-friend',
    [ScreenNames.LEVELS]: 'games/:gid/users/:uid/levels',
    [ScreenNames.TROPHIES]: 'games/:gid/users/:uid/trophies',
    [ScreenNames.TICKETZ]: 'games/:gid/ticketz',
    [ScreenNames.TICKETZ_ABOUT]: 'games/:gid/ticketz/about',
    [ScreenNames.SKILLZ_REWARDS]: 'games/:gid/ticketz/rewardz',
    [ScreenNames.SKILLZ_REWARDS_INFO]: 'games/:gid/ticketz/rewardz-info',
    [ScreenNames.TICKETZ_ITEM_DETAIL]: 'games/:gid/ticketz/item/:id',
    [ScreenNames.TICKETZ_ORDER_SUMMARY]: 'games/:gid/ticketz/item/:id/order-summary',
    [ScreenNames.TICKETZ_CATEGORY_LIST]: 'games/:gid/ticketz/category/:id',
    [ScreenNames.TICKETZ_EDIT_ADDRESS]: 'games/:gid/ticketz/edit-address',
    [ScreenNames.LIVE_EVENT]: 'games/:gid/live-event/:id',
    [ScreenNames.ACCOUNT]: 'games/:gid/account',
    [ScreenNames.ACCOUNT_BALANCE]: 'games/:gid/account/balance',
    // [ScreenNames.ACCOUNT_MERGE]: 'games/:gid/account/merge', // On web we have a modal for Merge Account
    [ScreenNames.ACCOUNT_PASSWORD]: 'games/:gid/account/change-password',
    [ScreenNames.ACCOUNT_PERSONAL]: 'games/:gid/account/personal-info',
    [ScreenNames.ACCOUNT_EMAIL_MANAGEMENT]: 'games/:gid/account/email',
    [ScreenNames.ACCOUNT_EMAIL_EDIT]: 'games/:gid/account/email/edit',
    [ScreenNames.ACCOUNT_ADDRESS]: 'games/:gid/account/address',
    [ScreenNames.ACCOUNT_PHONE]: 'games/:gid/account/phone',
    [ScreenNames.ACCOUNT_USERNAME]: 'games/:gid/account/change-username', // In SDK it's 'games/:gid/account/username'
    [ScreenNames.ACCOUNT_WITHDRAW]: 'games/:gid/account/withdraw',
    [ScreenNames.PRIZE_DETAILS]: 'games/:gid/prize-details/:tid',
    [ScreenNames.BRACKET_RESULTS]: 'games/:gid/brackets-results/:tid',
    [ScreenNames.BRACKETS_V2]: 'games/:gid/brackets-v2',
    [ScreenNames.BRACKET_VERSUS]: 'games/:gid/bracket-versus/:tid',
    [ScreenNames.VERSUS_COORDINATOR]: 'games/:gid/versus', // In SDK it's 'games/:gid/versus/:tid',
    [ScreenNames.PLAYER_PROFILE]: 'games/:gid/users/:uid/profile',
    [ScreenNames.DAILY_BONUS_CASH_SCREEN]: 'games/:gid/daily-bonus-cash',
  };

  private static patternToRegex(pattern: string): RegExp {
    const regexPattern = pattern
      .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
      .replace(/:[\w]+/g, '([^/]+)');

    return new RegExp(`^/${regexPattern}(/|\\?|$)`);
  }

  private static getCurrentPathname(): string {
    try {
      return window.location.pathname.replace(/^\//, '');
    } catch (error) {
      console.warn('NavigationManager: Unable to get current pathname', error);
      return '';
    }
  }

  static isSceneCurrentlyPresented(routeName?: string): boolean {
    return false; // work in progress

    // if (!routeName) {
    //   return false;
    // }

    // const pattern = NavigationManager.routePatterns[routeName];
    // if (!pattern) {
    //   return false;
    // }

    // const pathname = NavigationManager.getCurrentPathname();
    // if (!pathname) {
    //   return false;
    // }

    // const regex = NavigationManager.patternToRegex(pattern);
    // return regex.test(pathname);
  }

  // TODO: We have inconsistency with SDK here
  // In SDK isRouteDisplayed() checks the whole stack, which is not available on web
  // We can't also trust history as the user could jump around the page for longer time
  // so every route may exist there or not.
  // For now, we're only checking the current route and return false if there's no match,
  // just like isSceneCurrentlyPresented()
  static isRouteDisplayed(routeName?: string): boolean {
    return false; // work in progress
    // return NavigationManager.isSceneCurrentlyPresented(routeName);
  }

  // Same as isRouteDisplayed() - check comment above
  static isSceneInRoute(routeName?: string): boolean {
    return false; // work in progress
    // return NavigationManager.isSceneCurrentlyPresented(routeName);
  }

  // TODO: Implement this method / potentially remove it
  static checkViewControllerStack() {}

  // TODO: Implement this / if native navigation not used then remove
  private static _checkAndroidViewControllerStack() {}
  private static _checkiOSViewControllerStack() {}

  // TODO: Listeners for android native navigation
  popMainNavigatorToRootListener() {}
  // TODO: Listeners for android native navigation
  popNavigatorListener() {}
}

export default NavigationManager;
